import { TreeView } from "@/components/tree-view";
import { Skeleton } from "@/components/ui/skeleton";
import { DocumentCardSkeleton, EntityCard } from "@cohesive/ui-components";
import { useDebounce } from "@uidotdev/usehooks";
import { Globe, Users } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorEmpty,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import { useNotionMCPSearch } from "./useNotionMCPSearch";
import { useNotionMCPTeams } from "./useNotionMCPTeams";

interface TreeDataItem {
  id: string;
  name: string;
  icon?: any;
  teamspaceId?: string;
  showArchived?: boolean;
  onClick?: () => void;
}

export const NotionMCPSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [selectedTeam, setSelectedTeam] = useState<TreeDataItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 500);

  // Load teams
  const { data: teamsData, isLoading: isTeamsLoading } =
    useNotionMCPTeams(connection);

  // Parse teams from entities (filter out archived)
  const joinedTeams = useMemo(() => {
    const teams = teamsData || [];
    const joined: any[] = [];

    teams.forEach((team: any) => {
      if (team.base === "group" && team.data && !team.data.archived) {
        joined.push(team);
      }
    });

    return joined;
  }, [teamsData]);

  // Build tree data for sidebar
  const sidebarTreeData: TreeDataItem[] = useMemo(() => {
    const allTeamsItem: TreeDataItem = {
      id: "all-teams",
      name: "All Teams",
      icon: Globe,
      teamspaceId: undefined,
      onClick: () => setSelectedTeam(allTeamsItem),
    };

    const teamItems: TreeDataItem[] = joinedTeams.map((team: any) => ({
      id: team.data.id,
      name: team.data.name,
      icon: Users,
      teamspaceId: team.data.id,
      onClick: () =>
        setSelectedTeam({
          id: team.data.id,
          name: team.data.name,
          icon: Users,
          teamspaceId: team.data.id,
        }),
    }));

    return [allTeamsItem, ...teamItems];
  }, [joinedTeams]);

  // Set initial selected team to "All Teams"
  React.useEffect(() => {
    if (!selectedTeam && sidebarTreeData.length > 0) {
      setSelectedTeam(sidebarTreeData[0]);
    }
  }, [selectedTeam, sidebarTreeData]);

  // Search pages
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotionMCPSearch({
    connection,
    query: debouncedQuery,
    teamspaceId: selectedTeam?.teamspaceId,
  });

  // Flatten the typed search results from all pages (each page is the full
  // toolbox response now that entity_format=both is requested).
  const results = data?.pages?.flatMap((page) => page.data) ?? [];

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed page/database.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages?.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const handlePageSelect = (page: any) => {
    onSelect(page);
  };

  const resultIds = useMemo(
    () => results.map((p: any) => p.data?.id ?? String(p)),
    [results],
  );

  return (
    <Selector>
      <SelectorSidebar>
        {isTeamsLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView
            data={sidebarTreeData}
            onSelectChange={(item) => setSelectedTeam(item || null)}
            expandAll={false}
          />
        )}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={`Search ${selectedTeam?.name || "Notion"}...`}
        autoFocus
      />
      <SelectorResults
        onScroll={handleScroll}
        style={{ paddingBottom: "200px" }}
      >
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <DocumentCardSkeleton
                key={i}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        ) : isError ? (
          <SelectorEmpty message="Error loading pages" />
        ) : results.length === 0 ? (
          <SelectorEmpty message="No pages found" />
        ) : (
          <div className="divide-y">
            {results.map((page: any) => (
              <EntityDragRow
                key={page.data?.id || page}
                entity={page}
                generic={genericById.get(page.data?.id ?? String(page))}
                entityId={page.data?.id ?? String(page)}
                orderedIds={resultIds}
                onSelect={handlePageSelect}
                dragScope="notion-mcp"
              >
                <EntityCard
                  entity={page}
                  className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                />
              </EntityDragRow>
            ))}
            {/* Loading indicator for next page */}
            {isFetchingNextPage && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <DocumentCardSkeleton
                    key={`loading-${i}`}
                    className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </SelectorResults>
    </Selector>
  );
};

export default NotionMCPSelector;
